import { computed, ref, type Ref } from "vue";
import type { User } from "@/shared/generated/graphql";
import { CustomerAdditionalInfoType } from "@/shared/@types/CustomerAdditionalInfoType";
import { AddressType } from "@/shared/@types/AddressType";
import type { SalesOrganizationId } from "@/shared/@types/SalesOrganizationId";
import { getCustomer } from "@/shared/services/getCustomer.service";
import {
  type AccountAddress,
  mapToAccountAddressType,
} from "@/shared/@types/AccountAddress";

export const useGetAccountAddresses = (
  user: Ref<User | undefined>,
  selectedCustomerId: Ref<string | undefined>,
  currentSalesOrganizationId: Ref<SalesOrganizationId | undefined>,
) => {
  const accountAddresses = ref<AccountAddress[]>([]); // all addresses for customer id
  // all shipping-addresses filtered by sales organization id
  const shippingAddresses = computed<AccountAddress[]>(
    () =>
      accountAddresses.value.filter(
        (address) =>
          address.type === AddressType.Shipping &&
          address.salesOrganizationId === currentSalesOrganizationId.value,
      ) || [],
  );
  // all billing-addresses filtered by sales organization id
  const billingAddresses = computed<AccountAddress[]>(
    () =>
      accountAddresses.value.filter(
        (address) =>
          address.type === AddressType.Billing &&
          address.salesOrganizationId === currentSalesOrganizationId.value,
      ) || [],
  );

  const isFetchingAccountAddresses = ref(false);
  const fetchingAccountAddressesError = ref<string | undefined>(undefined);

  // TODO: Need to fetch addresses separately (because of timeout issues)
  // Need to implement pagination and search as soon as API supports it
  const onFetchingAccountAddresses = async () => {
    if (!user.value || !selectedCustomerId.value) return;

    try {
      isFetchingAccountAddresses.value = true;
      fetchingAccountAddressesError.value = undefined;

      const result = await getCustomer({
        userId: user.value.userId,
        customerId: selectedCustomerId.value,
        infoTypes: [CustomerAdditionalInfoType.Addresses],
      });

      accountAddresses.value =
        result.addresses?.map(mapToAccountAddressType) || [];
    } catch (error) {
      fetchingAccountAddressesError.value = error as string;
    } finally {
      isFetchingAccountAddresses.value = false;
    }
  };

  const resetAccountAddressData = () => {
    accountAddresses.value = [];
    isFetchingAccountAddresses.value = false;
    fetchingAccountAddressesError.value = undefined;
  };

  return {
    shippingAddresses,
    billingAddresses,
    isFetchingAccountAddresses,
    fetchingAccountAddressesError,
    onFetchingAccountAddresses,
    resetAccountAddressData,
  };
};
